import { useState, useEffect, useRef } from "react";
import { LoginPage } from "./LoginPage";
import { ReportsPage } from "./ReportsPage";
import { CalendarPage } from "./CalendarPage";
import "./index.css";

interface Drink {
  id: number;
  abv: number;
  ounces: number;
  units: number;
  created_at: string;
}

interface WeekDay {
  date: string;
  total: number;
}

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function todayStr() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function formatShortDate(dateStr: string) {
  const d = new Date(dateStr + "T12:00:00");
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function shiftWeek(dateStr: string, delta: number) {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + delta * 7);
  return d.toISOString().slice(0, 10);
}

export function App() {
  const [view, setView] = useState<"tracker" | "calendar" | "reports">("tracker");
  const [userId, setUserId] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [drinks, setDrinks] = useState<Drink[]>([]);
  const [total, setTotal] = useState(0);
  const [weekDays, setWeekDays] = useState<WeekDay[]>([]);
  const [weekTotal, setWeekTotal] = useState(0);
  const [monthTotalVal, setMonthTotalVal] = useState(0);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const abvRef = useRef<HTMLInputElement>(null);
  const ozRef = useRef<HTMLInputElement>(null);
  const unitsRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [inputMode, setInputMode] = useState<"detail" | "units">("detail");
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);

  const today = todayStr();
  const isToday = selectedDate === today;

  useEffect(() => {
    fetch("/api/auth/me")
      .then((r) => {
        if (r.ok) return r.json();
        throw new Error("not authenticated");
      })
      .then((d) => setUserId(d.userId))
      .catch(() => setUserId(null))
      .finally(() => setAuthChecked(true));
  }, []);

  async function fetchDrinks(date: string) {
    const res = await fetch(`/api/drinks?date=${date}`);
    if (!res.ok) return;
    const data = await res.json();
    setDrinks(data.drinks);
    setTotal(data.total);
    setWeekDays(data.week.days);
    setWeekTotal(data.week.weekTotal);
    setMonthTotalVal(data.monthTotal);
  }

  useEffect(() => {
    if (userId) fetchDrinks(selectedDate);
  }, [selectedDate, userId]);

  function updatePreview() {
    const abv = parseFloat(abvRef.current?.value || "");
    const oz = parseFloat(ozRef.current?.value || "");
    if (abv && oz) {
      setPreview(((abv * oz) / 60).toFixed(2));
    } else {
      setPreview(null);
    }
  }

  async function addDrink() {
    const body: Record<string, unknown> = {};

    if (inputMode === "units") {
      const units = parseFloat(unitsRef.current?.value || "");
      if (!units || units <= 0) return;
      body.units = units;
    } else {
      const abv = parseFloat(abvRef.current?.value || "");
      const oz = parseFloat(ozRef.current?.value || "");
      if (!abv || !oz) return;
      body.abv = abv;
      body.ounces = oz;
    }

    if (!isToday) body.date = selectedDate;

    try {
      const res = await fetch("/api/drinks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.drink) {
        setDrinks((prev) => [data.drink, ...prev]);
        setTotal(data.total);
        setWeekDays(data.week.days);
        setWeekTotal(data.week.weekTotal);
        setMonthTotalVal(data.monthTotal);
        if (abvRef.current) abvRef.current.value = "";
        if (ozRef.current) ozRef.current.value = "";
        if (unitsRef.current) unitsRef.current.value = "";
        setPreview(null);
      }
    } catch (err) {
      console.error("Failed to add drink:", err);
    }
  }

  async function deleteDrink(id: number) {
    const res = await fetch(`/api/drinks/${id}?date=${selectedDate}`, {
      method: "DELETE",
    });
    const data = await res.json();
    setDrinks((prev) => prev.filter((d) => d.id !== id));
    setTotal(data.total);
    setWeekDays(data.week.days);
    setWeekTotal(data.week.weekTotal);
    setMonthTotalVal(data.monthTotal);
  }

  async function handleLogout() {
    await fetch("/api/auth/logout", { method: "POST" });
    setUserId(null);
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwError("");
    setPwSuccess(false);
    const res = await fetch("/api/auth/change-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ currentPassword: pwCurrent, newPassword: pwNew }),
    });
    const data = await res.json();
    if (!res.ok) {
      setPwError(data.error);
      return;
    }
    setPwSuccess(true);
    setPwCurrent("");
    setPwNew("");
  }

  const monthLabel = new Date(selectedDate + "T12:00:00").toLocaleDateString(
    undefined,
    { month: "long", year: "numeric" }
  );

  const dayLabel = isToday
    ? "Today"
    : new Date(selectedDate + "T12:00:00").toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      });

  if (!authChecked) return null;

  if (!userId) {
    return <LoginPage onLogin={(id) => setUserId(id)} />;
  }

  return (
    <div>
      <div className="max-w-lg mx-auto p-4 pb-2">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-3xl font-bold">ALC Track</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setShowPasswordForm((v) => !v);
                setPwError("");
                setPwSuccess(false);
              }}
              className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              Password
            </button>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Nav toggle */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setView("tracker")}
            className={`flex-1 text-sm py-2 rounded-lg transition-colors cursor-pointer ${
              view === "tracker"
                ? "bg-blue-600 text-white"
                : "bg-[#16213e] text-gray-400 hover:text-white"
            }`}
          >
            Tracker
          </button>
          <button
            onClick={() => setView("calendar")}
            className={`flex-1 text-sm py-2 rounded-lg transition-colors cursor-pointer ${
              view === "calendar"
                ? "bg-blue-600 text-white"
                : "bg-[#16213e] text-gray-400 hover:text-white"
            }`}
          >
            Calendar
          </button>
          <button
            onClick={() => setView("reports")}
            className={`flex-1 text-sm py-2 rounded-lg transition-colors cursor-pointer ${
              view === "reports"
                ? "bg-blue-600 text-white"
                : "bg-[#16213e] text-gray-400 hover:text-white"
            }`}
          >
            Reports
          </button>
        </div>
      </div>

      {view === "reports" ? (
        <ReportsPage />
      ) : view === "calendar" ? (
        <CalendarPage />
      ) : (
      <div className="max-w-lg mx-auto p-4 pt-0">

      {/* Password change form */}
      {showPasswordForm && (
        <form
          onSubmit={handleChangePassword}
          className="bg-[#16213e] rounded-xl p-4 mb-4 space-y-3"
        >
          <div>
            <label className="block text-xs text-gray-400 mb-1">
              Current password
            </label>
            <input
              type="password"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              autoComplete="current-password"
              className="w-full bg-[#0f3460] rounded-lg px-3 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">
              New password
            </label>
            <input
              type="password"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              autoComplete="new-password"
              className="w-full bg-[#0f3460] rounded-lg px-3 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {pwError && <div className="text-red-400 text-sm">{pwError}</div>}
          {pwSuccess && (
            <div className="text-green-400 text-sm">Password updated</div>
          )}
          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg py-2 font-medium transition-colors cursor-pointer"
          >
            Change Password
          </button>
        </form>
      )}

      {/* Month total */}
      <div className="bg-[#16213e] rounded-xl px-4 py-3 mb-4 flex items-center justify-between">
        <span className="text-sm text-gray-400">{monthLabel}</span>
        <span className="font-bold">{monthTotalVal.toFixed(2)} units</span>
      </div>

      {/* Week bar */}
      <div className="bg-[#16213e] rounded-xl p-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => setSelectedDate((d) => shiftWeek(d, -1))}
            className="text-gray-400 hover:text-white px-2 py-1 text-lg cursor-pointer"
          >
            ‹
          </button>
          <span className="text-sm text-gray-400 font-medium">
            Week total: {weekTotal.toFixed(2)}
          </span>
          <button
            onClick={() => setSelectedDate((d) => shiftWeek(d, 1))}
            className="text-gray-400 hover:text-white px-2 py-1 text-lg cursor-pointer"
          >
            ›
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {weekDays.map((wd) => {
            const d = new Date(wd.date + "T12:00:00");
            const isSelected = wd.date === selectedDate;
            const isTodayCell = wd.date === today;
            return (
              <button
                key={wd.date}
                onClick={() => setSelectedDate(wd.date)}
                className={`flex flex-col items-center py-2 rounded-lg text-xs cursor-pointer transition-colors ${
                  isSelected
                    ? "bg-blue-600 text-white"
                    : isTodayCell
                      ? "bg-[#0f3460] text-blue-300"
                      : "hover:bg-[#0f3460] text-gray-400"
                }`}
              >
                <span className="font-medium">{DAY_NAMES[(d.getDay() + 6) % 7]}</span>
                <span className="text-[10px] opacity-70">
                  {formatShortDate(wd.date)}
                </span>
                <span className={`mt-1 font-bold text-sm ${wd.total > 0 ? "text-white" : "opacity-40"}`}>
                  {wd.total > 0 ? wd.total.toFixed(1) : "–"}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Day total */}
      <div className="bg-[#16213e] rounded-xl p-6 mb-4 text-center">
        <div className="text-sm uppercase tracking-wide text-gray-400 mb-1">
          {dayLabel}'s Units
        </div>
        <div className="text-5xl font-bold">{total.toFixed(2)}</div>
      </div>

      {/* Add drink form */}
      <form onSubmit={(e) => { e.preventDefault(); addDrink(); }} className="bg-[#16213e] rounded-xl p-4 mb-4">
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={() => setInputMode("detail")}
            className={`flex-1 text-xs py-1.5 rounded-lg transition-colors cursor-pointer ${
              inputMode === "detail"
                ? "bg-blue-600 text-white"
                : "bg-[#0f3460] text-gray-400 hover:text-white"
            }`}
          >
            ABV + Ounces
          </button>
          <button
            type="button"
            onClick={() => setInputMode("units")}
            className={`flex-1 text-xs py-1.5 rounded-lg transition-colors cursor-pointer ${
              inputMode === "units"
                ? "bg-blue-600 text-white"
                : "bg-[#0f3460] text-gray-400 hover:text-white"
            }`}
          >
            Units
          </button>
        </div>
        {inputMode === "detail" ? (
          <>
            <div className="flex gap-3 mb-3">
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">ABV %</label>
                <input
                  ref={abvRef}
                  type="number"
                  step="0.1"
                  min="0"
                  autoComplete="off"
                  onInput={updatePreview}
                  className="w-full bg-[#0f3460] rounded-lg px-3 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Ounces</label>
                <input
                  ref={ozRef}
                  type="number"
                  step="0.1"
                  min="0"
                  autoComplete="off"
                  onInput={updatePreview}
                  className="w-full bg-[#0f3460] rounded-lg px-3 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            {preview && (
              <div className="text-sm text-gray-400 mb-3">= {preview} units</div>
            )}
          </>
        ) : (
          <div className="mb-3">
            <label className="block text-xs text-gray-400 mb-1">Units</label>
            <input
              ref={unitsRef}
              type="number"
              step="0.1"
              min="0"
              autoComplete="off"
              className="w-full bg-[#0f3460] rounded-lg px-3 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}
        <button
          type="submit"
          className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg py-2 font-medium transition-colors cursor-pointer"
        >
          Add Drink{!isToday && ` to ${dayLabel}`}
        </button>
      </form>

      {/* Drink log */}
      {drinks.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm uppercase tracking-wide text-gray-400 mb-2">
            {dayLabel}'s Log
          </h2>
          {drinks.map((drink) => (
            <div
              key={drink.id}
              className="bg-[#16213e] rounded-lg px-4 py-3 flex items-center justify-between"
            >
              <div>
                <span className="font-medium">
                  {drink.units.toFixed(2)} units
                </span>
                {drink.abv > 0 && drink.ounces > 0 && (
                  <span className="text-gray-400 text-sm ml-2">
                    {drink.ounces}oz @ {drink.abv}%
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">
                  {new Date(drink.created_at).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
                <button
                  onClick={() => deleteDrink(drink.id)}
                  className="text-gray-500 hover:text-red-400 transition-colors text-lg leading-none cursor-pointer"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
      )}
    </div>
  );
}

export default App;
